import { useState, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Box,
  Typography,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Skeleton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

type Props = {
  values: string[];
  isLoading: boolean;
  onDelete: (value: string) => void;
};

export const ActionCommandsList = ({ values, isLoading, onDelete }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleConfirmDelete = useCallback(() => {
    if (deleteTarget) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  }, [deleteTarget, onDelete]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="rounded" height={48} />
        ))}
      </Box>
    );
  }

  if (values.length === 0) {
    return (
      <Paper
        elevation={1}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center'
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t('settings:administration.tools.actionCommands.emptyState')}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <List sx={{ py: 0 }}>
          {values.map((value, index) => {
            const isLastItem = index === values.length - 1;

            return (
              <ListItem
                key={value}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => setDeleteTarget(value)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  py: 1.5,
                  px: 2,
                  borderBottom: !isLastItem
                    ? `1px solid ${theme.palette.divider}`
                    : 'none',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                <ListItemText primary={value} />
              </ListItem>
            );
          })}
        </List>
      </Paper>

      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>
          {t('settings:administration.tools.actionCommands.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'settings:administration.tools.actionCommands.deleteDialog.message',
              { value: deleteTarget }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>
            {t(
              'settings:administration.tools.actionCommands.deleteDialog.cancel'
            )}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {t(
              'settings:administration.tools.actionCommands.deleteDialog.confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
